<template>
  <section
    id="capabilities"
    ref="capabilitiesSection"
    class="landing-container lt-sm:px-0 scroll-mt-70px py-112px lt-sm:py-120px overflow-hidden bg-[linear-gradient(0deg,#f1f7ff_0%,#fff_100%)] transition-all duration-1000 ease-out"
    :class="[isCapabilitiesVisible ? 'translate-y-0 opacity-100' : 'translate-y-50px opacity-0']"
    data-section
  >
    <section-heading
      title="一套平台，贯通物业运营全链路"
      class="lt-sm:px-32px"
      description="优先解决档案和收费两大高频刚需，再延伸到工单、巡检、硬件、人事与移动 OA。"
    />

    <div
      class="mb-56px border-#e6eef9 bg-#f6faff p-8px scrollbar-none lt-sm:hidden sm:gap-8px mx-auto flex w-fit max-w-full overflow-x-auto rounded-full border shadow-[0_6px_22px_rgba(15,103,248,0.05),inset_0_0_0_1px_rgba(255,255,255,0.8)]"
      role="tablist"
      aria-label="核心能力"
    >
      <button
        v-for="(item, index) in capabilities"
        :id="`capability-tab-${item.key}`"
        :key="item.key"
        class="pf-sc-semibold min-h-44px min-w-108px px-18px font-s-14px lt-sm:min-h-72px lt-sm:min-w-168px lt-sm:px-24px lt-sm:font-s-24px rounded-full border-0 bg-transparent whitespace-nowrap transition-all duration-200 hover:bg-white/70 hover:shadow-[0_1px_4px_rgba(15,103,248,0.08)]"
        :class="
          activeCapability === item.key
            ? 'text-#0f67f8 bg-white shadow-[0_1px_4px_rgba(15,103,248,0.08)]'
            : 'text-#1e293b hover:text-#0f67f8'
        "
        type="button"
        role="tab"
        aria-controls="capability-panel"
        :aria-selected="activeCapability === item.key"
        :tabindex="activeCapability === item.key ? 0 : -1"
        @click="emit('select', item.key)"
        @keydown="emit('keydown', $event, index)"
      >
        {{ item.label }}
      </button>
    </div>

    <div
      ref="mobileList"
      class="capability-mobile-list lt-sm:flex lt-sm:h-auto lt-sm:gap-16px lt-sm:overflow-x-auto lt-sm:overflow-y-hidden lt-sm:px-24px lt-sm:[scroll-snap-type:x_mandatory] lt-sm:[scrollbar-width:none] hidden"
      aria-label="核心能力"
      @scroll.passive="handleMobileScroll"
    >
      <article
        v-for="(item, index) in capabilities"
        :key="item.key"
        class="capability-mobile-card lt-sm:min-h-790px lt-sm:w-630px lt-sm:flex-[0_0_90%] lt-sm:[scroll-snap-align:center] lt-sm:rounded-24px lt-sm:px-36px lt-sm:pt-40px relative overflow-hidden transition-all duration-600"
        :class="
          activeCapability === item.key
            ? 'is-active lt-sm:scale-100 lt-sm:opacity-100 bg-[linear-gradient(0deg,#E5E9F5_0%,#EFF2FB_100%)]'
            : 'lt-sm:scale-96 lt-sm:opacity-50 bg-[linear-gradient(0deg,#E5E9F5_0%,#EFF2FB_100%)]'
        "
        :aria-current="activeCapability === item.key ? 'true' : undefined"
        data-capability-card
        @click="scrollToMobileCapability(index)"
      >
        <div class="relative z-1">
          <div class="gap-8px flex items-center justify-center">
            <p class="pf-sc-semibold text-#0f67f8 lt-sm:font-s-40px m-0">
              {{ item.label }}
            </p>
            <h3 class="pf-sc-semibold text-#0f172a lt-sm:font-s-40px">
              {{ item.title }}
            </h3>
          </div>

          <div class="mt-40px gap-18px grid">
            <section v-for="detail in item.details" :key="detail.title">
              <div class="gap-8px lt-sm:gap-16px flex items-center">
                <i
                  class="i-custom-check-one solution-card-item-icon wh-18px lt-sm:wh-32px shrink-0"
                  aria-hidden="true"
                ></i>
                <div>
                  <h4
                    class="pf-sc-semibold font-s-18px text-#0f172a lt-sm:font-s-26px lt-sm:leading-[36px] m-0 leading-[1.35]"
                  >
                    {{ detail.title }}
                  </h4>
                  <p
                    class="pf-sc-regular mt-6px font-s-14px text-#64748b lt-sm:font-s-22px lt-sm:leading-[34px] m-0 leading-[1.7] break-words"
                  >
                    {{ detail.text }}
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>

        <img
          class="lt-sm:w-450px lt-sm:m-auto max-w-none drop-shadow-[0_16px_18px_rgba(15,103,248,0.12)] transition-all duration-300"
          :src="item.image"
          :alt="`${item.label}${item.title}示意图`"
          loading="lazy"
        />
      </article>
    </div>

    <div class="capability-dots lt-sm:mt-40px lt-sm:flex lt-sm:gap-16px flex-center hidden">
      <button
        v-for="(item, index) in capabilities"
        :key="item.key"
        class="capability-dot lt-sm:h-10px lt-sm:w-60px bg-#E5E9F5 rounded-full border-0 p-0 transition-all"
        :class="{ active: activeCapability === item.key }"
        type="button"
        :aria-label="`查看${item.label}`"
        :aria-current="activeCapability === item.key ? 'true' : undefined"
        @click="scrollToMobileCapability(index)"
      ></button>
    </div>

    <article
      id="capability-panel"
      class="min-h-586px w-1440px gap-54px lt-sm:hidden relative left-1/2 grid translate-x-[-50%] grid-cols-[664px_722px] items-center"
      role="tabpanel"
      tabindex="0"
      :aria-labelledby="`capability-tab-${currentCapability.key}`"
    >
      <div>
        <h3
          class="pf-sc-semibold mb-22px font-s-40px text-#0f172a lt-sm:font-s-48px lt-sm:leading-[60px] m-0 leading-[1.2]"
        >
          <span class="text-#0f67f8">{{ currentCapability.label }}</span>
          {{ currentCapability.title }}
        </h3>
        <div class="mb-48px gap-18px lt-sm:mb-48px lt-sm:gap-24px flex flex-wrap">
          <span
            v-for="tag in currentCapability.tags"
            :key="tag"
            class="pf-sc-semibold font-s-15px text-#17a766 before:mr-8px before:h-10px before:w-10px before:bg-#17c67d lt-sm:font-s-24px lt-sm:before:h-14px lt-sm:before:w-14px inline-flex items-center before:rounded-full before:content-['']"
          >
            {{ tag }}
          </span>
        </div>
        <div class="gap-28px grid">
          <section v-for="detail in currentCapability.details" :key="detail.title">
            <h4
              class="pf-sc-semibold mb-10px font-s-22px text-#0f172a lt-sm:font-s-32px lt-sm:leading-[40px] m-0 leading-[1.35]"
            >
              {{ detail.title }}
            </h4>
            <p
              class="pf-sc-regular font-s-16px text-#64748b lt-sm:font-s-24px lt-sm:leading-[40px] m-0 leading-[1.85] break-words"
            >
              {{ detail.text }}
            </p>
          </section>
        </div>
      </div>

      <div class="min-h-586px w-722px relative" aria-hidden="true">
        <transition name="capability-image" mode="out-in">
          <img
            :key="currentCapability.key"
            class="hover:-translate-y-8px w-722px ml-auto block max-w-none drop-shadow-[0_16px_12px_rgba(0,0,0,0.06)] transition-all duration-280 hover:drop-shadow-[0_24px_18px_rgba(15,103,248,0.12)]"
            :src="currentCapability.image"
            alt=""
          />
        </transition>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
  import type { Capability } from '~/constants/huiguanjia-home'

  import { nextTick, onBeforeUnmount, useTemplateRef } from 'vue'

  import SectionHeading from '../section-heading.vue'

  import { capabilities } from '~/constants/huiguanjia-home'

  defineOptions({
    name: 'CapabilitiesSection',
  })

  const props = defineProps<{
    activeCapability: string
    currentCapability: Capability
  }>()

  const emit = defineEmits<{
    keydown: [event: KeyboardEvent, index: number]
    select: [key: string]
  }>()

  const mobileList = useTemplateRef<HTMLElement>('mobileList')
  const capabilitiesSection = useTemplateRef<HTMLElement>('capabilitiesSection')
  const { isVisible: isCapabilitiesVisible } = useScrollReveal(capabilitiesSection)

  let mobileScrollFrame: number | undefined

  const getMobileCards = () => {
    return [...(mobileList.value?.querySelectorAll<HTMLElement>('[data-capability-card]') ?? [])]
  }

  const scrollToMobileCapability = (index: number) => {
    const item = capabilities[index]
    if (!item) return

    emit('select', item.key)

    nextTick(() => {
      const list = mobileList.value
      const card = getMobileCards()[index]
      if (!list || !card) return

      list.scrollTo({
        left: card.offsetLeft - (list.clientWidth - card.offsetWidth) / 2,
        behavior: 'smooth',
      })
    })
  }

  const syncActiveCapabilityFromScroll = () => {
    const list = mobileList.value
    if (!list) return

    const cards = getMobileCards()
    const listCenter = list.scrollLeft + list.clientWidth / 2
    let nearestIndex = 0
    let nearestDistance = Number.POSITIVE_INFINITY

    cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2
      const distance = Math.abs(cardCenter - listCenter)
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearestIndex = index
      }
    })

    const item = capabilities[nearestIndex]
    if (item && item.key !== props.activeCapability) {
      emit('select', item.key)
    }
  }

  const handleMobileScroll = () => {
    if (typeof window === 'undefined') return

    if (mobileScrollFrame) {
      window.cancelAnimationFrame(mobileScrollFrame)
    }

    mobileScrollFrame = window.requestAnimationFrame(() => {
      mobileScrollFrame = undefined
      syncActiveCapabilityFromScroll()
    })
  }

  onBeforeUnmount(() => {
    if (mobileScrollFrame && typeof window !== 'undefined') {
      window.cancelAnimationFrame(mobileScrollFrame)
    }
  })
</script>

<style scoped>
  .capability-mobile-list::-webkit-scrollbar {
    display: none;
  }

  .capability-mobile-card:first-child {
    @apply lt-sm:ml-36px;
  }

  .capability-mobile-card:last-child {
    @apply lt-sm:mr-36px;
  }

  .capability-image-enter-active,
  .capability-image-leave-active {
    transition:
      opacity 0.22s ease,
      transform 0.22s ease;
  }

  .capability-image-enter-from,
  .capability-image-leave-to {
    opacity: 0;
    transform: translateY(10px) scale(0.985);
  }

  @screen lt-sm {
    .capability-dot.active {
      background-color: #0f67f8 !important;
    }
  }
</style>
